import React, { createContext, useContext, useState, useEffect } from 'react'
import { AuthService } from '../lib/auth'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { session } = await AuthService.getCurrentSession()
        setUser(session?.user ?? null)
      } catch (error) {
        console.error('Error getting initial session:', error)
        setError(error.message)
      } finally {
        setLoading(false)
      }
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = AuthService.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (event === 'SIGNED_OUT') {
        setError(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Handle invalid refresh token by forcing a clean sign out to re-authenticate
  useEffect(() => {
    const handleTokenError = async (e) => {
      try {
        const msg = (e && e.message) || ''
        if (msg && msg.toLowerCase().includes('invalid refresh token')) {
          await AuthService.signOut()
          setUser(null)
        }
      } catch (_) {}
    }
    window.addEventListener('supabase-auth-error', handleTokenError)
    return () => window.removeEventListener('supabase-auth-error', handleTokenError)
  }, [])

  const signIn = async (email, password) => {
    try {
      setError(null)
      setLoading(true)
      const { data, error } = await AuthService.signIn(email, password)
      
      if (error) {
        setError(error.message)
        return { success: false, error: error.message }
      }
      
      return { success: true, data }
    } catch (error) {
      const errorMessage = error.message || 'An unexpected error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  const signUp = async (email, password, userData = {}) => {
    try {
      setError(null)
      setLoading(true)
      const { data, error } = await AuthService.signUp(email, password, userData)
      
      if (error) {
        setError(error.message)
        return { success: false, error: error.message }
      }
      
      return { success: true, data }
    } catch (error) {
      const errorMessage = error.message || 'An unexpected error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setError(null)
      setLoading(true)
      const { error } = await AuthService.signOut()
      
      if (error) {
        setError(error.message)
        return { success: false, error: error.message }
      }
      
      return { success: true }
    } catch (error) {
      const errorMessage = error.message || 'An unexpected error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email) => {
    try {
      setError(null)
      const { error } = await AuthService.resetPassword(email)
      
      if (error) {
        setError(error.message)
        return { success: false, error: error.message }
      }
      
      return { success: true }
    } catch (error) {
      const errorMessage = error.message || 'An unexpected error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  const updatePassword = async (newPassword) => {
    try {
      setError(null)
      setLoading(true)
      const { error } = await AuthService.updatePassword(newPassword)
      
      if (error) {
        setError(error.message)
        return { success: false, error: error.message }
      }
      
      return { success: true }
    } catch (error) {
      const errorMessage = error.message || 'An unexpected error occurred'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  const clearError = () => {
    setError(null)
  }

  const value = {
    user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    clearError,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
